/**
 * CustomEtapas.tsx — o acompanhamento da autorização: quem manda na lista de
 * etapas, no painel de criar e no formulário de concluir.
 *
 * Nasceu em 04/09/2026 (ficha #26) de duas queixas que dizem a mesma coisa:
 *
 *   SUANY: "seria muito interessante um passo a passo de etapas, igual às
 *   bolinhas do pedido de venda — eu marco a minha parte como feita e dou
 *   seguimento para a Patrícia… senão fica a cargo de eu e Patrícia lembrar."
 *
 *   PATRÍCIA: "seria interessante ter as tagzinhas, as cores: pendente,
 *   aguardando autorização, aguardando finalização."
 *
 * ─── NADA DE MODAL. NEM PARA A ETAPA, NEM PARA CRIAR ────────────────────────
 * O modal chegou a ser a resposta a um defeito real (painel embaixo da grade
 * não se percebe), e durou um dia. Ele o descartou pelo que o modal NÃO cabe:
 * *"não vai ser um modal porque o modal não vai ficar bom — não vai ter espaço
 * para ter conversa, para você ter um chat"*. A etapa tem conversa própria; o
 * lugar dela é uma PÁGINA (`/autorizacoes/:id/etapas/:etapaId`).
 *
 * O que ficou aqui, na aba, é a versão de fórum: a lista das etapas, com as
 * últimas linhas da conversa e um campo de comentar no rodapé de cada card, e
 * um painel de criação que abre no meio da lista — o "Your Answer" do tema.
 *
 * ─── O ESTADO É CALCULADO NO BACKEND ────────────────────────────────────────
 * `na_vez` (aberta e sem dependência aberta na frente) vem pronto da API — a
 * tela nunca decide isso sozinha, senão duas telas discordariam.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api, ApiError } from '@/lib/api';
import { formatarData } from '@/lib/data';
import { useAuth } from '@/auth/auth-context';
import { CustomCampoData } from '@/components/CustomCamposData';
import { CustomEtapasLista } from './CustomEtapasLista';
import { CustomEtapaConclusao } from './CustomEtapaConclusao';
import type { UsuarioOpcao } from './shared';
import { etapaAberta } from './shared';

/** Uma das últimas linhas da conversa, como o card a mostra. */
export interface LinhaDaPrevia {
  id: number;
  origem: 'pessoa' | 'sistema';
  autor_nome?: string | null;
  autor_foto?: string | null;
  texto: string;
  criacao?: string | null;
}

/** A prévia da conversa da etapa — o suficiente para responder do card. */
export interface ConversaPrevia {
  sala_id: number;
  titulo: string;
  estado: 'aberta' | 'fechada' | 'arquivada';
  total: number;
  nao_lidas: number;
  posso_falar: boolean;
  ultimas: LinhaDaPrevia[];
}

export interface Etapa {
  id: number;
  autorizacao_id: number;
  titulo: string;
  detalhe?: string | null;
  responsavel_id?: number | null;
  responsavel_nome?: string | null;
  responsavel_foto?: string | null;
  /* O que o BANCO guarda. A TELA mostra cinco rótulos (091): `aberta` vira
     **Bloqueada** ou **Liberada** conforme `na_vez`. Ver `estadoDaEtapa`. */
  estado: 'aberta' | 'em_andamento' | 'feita' | 'dispensada';
  /** Calculado no backend: aberta e sem dependência aberta na frente. */
  na_vez: boolean;
  prazo?: string | null;
  vencida: boolean;
  adiamentos: number;
  observacao_conclusao?: string | null;
  conclusao?: string | null;
  concluinte_nome?: string | null;
  ordem: number;
  criado_em?: string | null;
  criador_nome?: string | null;
  depende_de: number[];
  /** Só vem quando a etapa já tem sala e a pessoa a enxerga. */
  conversa?: ConversaPrevia | null;
  /** A última linha do registro (088) — é ela que diz em que pé a etapa está. */
  ultima_atividade?: {
    id: number;
    tipo: string;
    relato: string | null;
    criacao: string | null;
    autor_nome: string;
    autor_foto: string | null;
  } | null;
  /** Quantas atividades a etapa tem — o card mostra os DOIS números. */
  total_atividades?: number;
}

interface Props {
  autorizacaoId: number;
  usuarios: UsuarioOpcao[];
  /** Quem pode criar, atribuir e concluir (`autorizacoes.etapas.gerir`). */
  podeGerir: boolean;
  aoMudar?: () => void;
}

export function CustomEtapas({ autorizacaoId, usuarios, podeGerir, aoMudar }: Props) {
  const { usuario } = useAuth();
  const navegar = useNavigate();
  const [etapas, setEtapas] = useState<Etapa[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  /** Criando: `null` = fechado; `0` = do zero; id = derivando daquela etapa. */
  const [criandoApos, setCriandoApos] = useState<number | null>(null);
  const [nTitulo, setNTitulo] = useState('');
  const [nResponsavel, setNResponsavel] = useState('');
  const [nDetalhe, setNDetalhe] = useState('');
  const [nPrazo, setNPrazo] = useState('');
  const [nDepende, setNDepende] = useState<number[]>([]);
  const [salvando, setSalvando] = useState(false);

  /** Concluindo: o formulário abre DENTRO do card daquela etapa. */
  const [concluindo, setConcluindo] = useState<number | null>(null);

  const carregar = useCallback(async () => {
    try {
      const lista = await api.get<Etapa[]>(`/autorizacoes/${autorizacaoId}/etapas`);
      setEtapas(lista);
      setErro(null);
    } catch (err) {
      // 403 é normal: as etapas são cegas para quem não acompanha.
      setErro(err instanceof ApiError && err.status === 403 ? '' : 'Não deu para carregar as etapas.');
    } finally {
      setCarregando(false);
    }
  }, [autorizacaoId]);

  useEffect(() => { carregar(); }, [carregar]);

  const depois = useCallback(() => {
    carregar();
    aoMudar?.();
  }, [carregar, aoMudar]);

  function abrirCriacao(apos: Etapa | null) {
    setConcluindo(null);
    setNTitulo(''); setNDetalhe(''); setNPrazo('');
    /* ⛔ QUEM CRIA JÁ NASCE RESPONSÁVEL — dele, 12/09/2026, depois de usar:
       *"eu acho que quem cria a próxima etapa… o responsável tem que ser a
       pessoa que está criando"*.

       A regra vale para as DUAS portas, e a segunda era a que doía: "Próxima
       etapa" herdava o dono da anterior, então criar a seguinte de uma etapa da
       Suany já nascia delegada para a Suany sem ninguém escolher isso —
       *"na hora que eu criei a próxima etapa foi automaticamente delegada para
       a Suany. Eu acho que não."*

       O raciocínio dele, e é o certo: *"a Patrícia vai criar uma etapa para a
       Suany? Pouco provável. Provável é que ela crie uma etapa para ela mesma.
       E se ela achar que a etapa deve ser da Suany, ela marca."*

       É palpite, não trava: o campo continua aberto logo abaixo. */
    setNResponsavel(usuario?.id ? String(usuario.id) : '');
    if (apos) {
      // Derivar: a nova espera aquela etapa.
      setNDepende([apos.id]);
      setCriandoApos(apos.id);
    } else {
      const abertas = etapas.filter((e) => e.estado === 'aberta' || e.estado === 'em_andamento');
      const ultima = abertas[abertas.length - 1];
      setNDepende(ultima ? [ultima.id] : []);
      setCriandoApos(0);
    }
  }

  async function criar() {
    if (!nTitulo.trim()) return;
    setSalvando(true);
    try {
      await api.post(`/autorizacoes/${autorizacaoId}/etapas`, {
        titulo: nTitulo.trim(),
        detalhe: nDetalhe.trim() || null,
        responsavel_id: nResponsavel ? Number(nResponsavel) : null,
        prazo: nPrazo || null,
        depende_de: nDepende,
      });
      setCriandoApos(null);
      depois();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não deu para criar a etapa.');
    } finally {
      setSalvando(false);
    }
  }

  async function acao(caminho: string, corpo?: unknown, metodo: 'post' | 'delete' = 'post') {
    try {
      if (metodo === 'delete') await api.delete(caminho);
      else await api.post(caminho, corpo);
      depois();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não deu para fazer isso.');
    }
  }

  if (carregando) {
    return (
      <div className="d-flex align-items-center gap-2 text-secondary small py-2">
        <span className="spinner-border spinner-border-sm" /> Carregando…
      </div>
    );
  }
  if (erro === '') return null;
  if (erro) return <div className="alert alert-light small mb-0">{erro}</div>;

  /* ─── O PAINEL DE CRIAR — o "Your Answer" do fórum ───────────────────── */
  const origem = criandoApos ? etapas.find((e) => e.id === criandoApos) ?? null : null;
  const painelCriar = criandoApos === null ? null : (
    <div className="card adminuiux-card casaa-etapa-nova mb-3 mb-lg-4">
      <div className="card-header bg-transparent">
        <h6 className="mb-0">
          <i className="bi bi-plus-circle text-theme-1 me-2"></i>
          {origem ? `Próxima etapa depois de “${origem.titulo}”` : 'Nova etapa'}
        </h6>
      </div>
      <div className="card-body">
        <div className="row g-3">
          <div className="col-12">
            <div className="form-floating">
              <input id="etapa-titulo" className="form-control" placeholder=" "
                     value={nTitulo} onChange={(ev) => setNTitulo(ev.target.value)}
                     maxLength={200} autoFocus />
              <label htmlFor="etapa-titulo">O que precisa ser feito</label>
            </div>
          </div>
          {/* EXPLICAR NA HORA DE CRIAR — pedido dele, 05/09: *"não vejo por que
              você tem que criar a etapa e depois entrar, abrir e explicar o que
              é. Acho isso ruim."* Quem está criando é quem sabe o combinado; a
              explicação some se ficar para depois. */}
          <div className="col-12">
            <div className="form-floating">
              <textarea id="etapa-detalhe" className="form-control" placeholder=" "
                        style={{ height: '6rem' }}
                        value={nDetalhe} onChange={(ev) => setNDetalhe(ev.target.value)} />
              <label htmlFor="etapa-detalhe">Explique a etapa (opcional)</label>
            </div>
          </div>
          <div className="col-sm-6">
            <div className="form-floating">
              <select id="etapa-responsavel" className="form-select" value={nResponsavel}
                      onChange={(ev) => setNResponsavel(ev.target.value)}>
                <option value="">— sem responsável ainda —</option>
                {usuarios.map((u) => (
                  <option key={u.id} value={String(u.id)}>{u.nome}</option>
                ))}
              </select>
              <label htmlFor="etapa-responsavel">Responsável</label>
            </div>
          </div>
          <div className="col-sm-6">
            {/* Prazo é opcional de propósito: "no próximo pedido de compra"
                não tem data, e é justamente o caso que mais some. */}
            <CustomCampoData id="etapa-prazo" label="Prazo (opcional)" valor={nPrazo}
                             onChange={setNPrazo} />
          </div>

          {etapas.length > 0 && (
            <div className="col-12">
              <label className="form-label small text-secondary mb-2">
                Só começa depois de… <span className="fst-italic">(marque duas para juntar duas pontas)</span>
              </label>
              <div className="d-flex flex-wrap gap-2">
                {etapas.map((e) => (
                  <button
                    key={e.id}
                    type="button"
                    className={`btn btn-sm ${nDepende.includes(e.id) ? 'btn-theme' : 'btn-outline-secondary'}`}
                    onClick={() =>
                      setNDepende((atual) =>
                        atual.includes(e.id) ? atual.filter((x) => x !== e.id) : [...atual, e.id],
                      )
                    }
                  >
                    {e.titulo.length > 30 ? `${e.titulo.slice(0, 30)}…` : e.titulo}
                  </button>
                ))}
              </div>
              <p className="small text-secondary mt-2 mb-0">
                {nDepende.length === 0
                  ? 'Nada marcado: esta etapa começa junto com as primeiras — vai na mesma linha delas.'
                  : nDepende.length > 1
                    ? 'Duas ou mais: a etapa só acende quando todas fecharem.'
                    : 'Ela entra na linha de baixo. Marcando a mesma anterior em duas etapas, a linha se divide em duas colunas.'}
              </p>
            </div>
          )}
        </div>
      </div>
      <div className="card-footer bg-transparent d-flex gap-2">
        <button type="button" className="btn btn-theme" onClick={criar}
                disabled={salvando || !nTitulo.trim()}>
          {salvando ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-check2 me-1"></i>}
          Criar etapa
        </button>
        <button type="button" className="btn btn-link text-danger ms-auto"
                onClick={() => setCriandoApos(null)}>
          <i className="bi bi-trash3 me-1"></i>Descartar
        </button>
      </div>
    </div>
  );

  /* ─── O RESUMO DA LATERAL ─────────────────────────────────────────────────
     A lista de fórum do tema NUNCA ocupa a página inteira: ela vive em
     `col-xl-9`, com uma coluna de 3 ao lado ("Featured Questions"). Sem essa
     coluna, uma etapa sozinha estica o card — e o campo de registro — por
     1440px, que é o que fazia o card não parecer o do tema por mais fiel que
     fosse o markup. A coluna aqui responde as três perguntas que a lista não
     responde de relance: de quem é a bola, o que vence primeiro, quanto falta. */
  const abertas = etapas.filter(etapaAberta);
  /* Com a bola = quem já assumiu, mais quem está liberado para assumir. */
  const naVez = abertas.filter((e) => e.estado === 'em_andamento' || e.na_vez);
  const feitas = etapas.length - abertas.length;
  const proximoPrazo = abertas
    .filter((e) => e.prazo)
    .sort((a, b) => (a.prazo! < b.prazo! ? -1 : 1))[0] ?? null;
  const responsaveisDaVez = Array.from(
    new Map(naVez.map((e) => [e.responsavel_id ?? 0, e])).values(),
  );

  const lateral = etapas.length === 0 ? null : (
    <div className="card adminuiux-card bg-l-gradient-light mb-3 mb-lg-4">
      <div className="card-header border-bottom">
        <h6 className="mb-0"><i className="bi bi-signpost-split me-2"></i>Como está</h6>
      </div>
      <div className="card-body">
        <p className="text-secondary small mb-1">Responsável agora</p>
        {responsaveisDaVez.length === 0 ? (
          <p className="mb-3">
            {abertas.length === 0 ? 'Ninguém — tudo fechado.' : 'Ninguém: as abertas esperam outra etapa.'}
          </p>
        ) : (
          <div className="mb-3">
            {responsaveisDaVez.map((e) => (
              <div className="d-flex align-items-center gap-2 mb-2" key={e.id}>
                {e.responsavel_foto ? (
                  <figure className="avatar avatar-30 rounded-circle casaa-foto-no-avatar mb-0">
                    <img src={e.responsavel_foto} alt="" />
                  </figure>
                ) : (
                  <span className="avatar avatar-30 rounded-circle bg-theme-1-subtle text-theme-1">
                    <i className="bi bi-person"></i>
                  </span>
                )}
                <span>{e.responsavel_nome ?? 'sem responsável'}</span>
              </div>
            ))}
          </div>
        )}

        {proximoPrazo && (
          <>
            <p className="text-secondary small mb-1">Vence primeiro</p>
            <p className={`mb-3 ${proximoPrazo.vencida ? 'text-danger' : ''}`}>
              {proximoPrazo.titulo}
              <small className="d-block text-secondary">{formatarData(proximoPrazo.prazo!)}</small>
            </p>
          </>
        )}

        <p className="text-secondary small mb-1">Andamento</p>
        <h5 className="mb-0">{feitas} de {etapas.length}</h5>
      </div>
    </div>
  );

  return (
    <>
      {/* A AÇÃO PRINCIPAL FICA NO TOPO, com peso de botão primário. Antes era um
          link apagado embaixo da lista, e ele não achava: "não dá para ver onde
          você cria uma etapa". */}
      <div className="d-flex align-items-center gap-2 mb-3">
        <div className="flex-grow-1">
          <p className="text-secondary small mb-0">
            {etapas.length === 0
              ? 'O que ficou combinado — falar com o fornecedor, refazer o pedido, abater no próximo pagamento — vira uma etapa aqui, com responsável e prazo.'
              : 'Cada etapa tem o seu chat. As últimas falas aparecem no card, e dá para responder sem sair daqui.'}
          </p>
        </div>
        {podeGerir && (
          <button type="button" className="btn btn-theme" onClick={() => abrirCriacao(null)}>
            <i className="bi bi-plus-lg me-1"></i>
            {etapas.length ? 'Nova etapa' : 'Criar a primeira etapa'}
          </button>
        )}
      </div>

      <div className="row">
        <div className="col-12 col-lg-8 col-xl-9">
      <CustomEtapasLista
        etapas={etapas}
        usuarios={usuarios}
        autorizacaoId={autorizacaoId}
        podeGerir={podeGerir}
        aoMudar={depois}
        aoDerivar={(e) => abrirCriacao(e)}
        aoConcluir={(e) => {
          setCriandoApos(null);
          setConcluindo(e.id);
        }}
        /* ADIAR PEDE O MOTIVO desde 05/09 — e o motivo é texto, que não cabe
           num item de menu. Daqui, manda para a página da etapa, onde o campo
           existe: adiar sem dizer por quê era justamente o que ele cortou. */
        aoAdiar={(e) => navegar(`/autorizacoes/${autorizacaoId}/etapas/${e.id}`)}
        aoReabrir={(e) => acao(`/autorizacoes/etapas/${e.id}/reabertura`)}
        aoExcluir={(e) => acao(`/autorizacoes/etapas/${e.id}`, undefined, 'delete')}
        /* O formulário de concluir abre dentro do card da própria etapa. */
        slotNoCard={(e) => (concluindo === e.id ? (
          <div className="mt-3">
            <CustomEtapaConclusao
              etapaId={e.id}
              usuarios={usuarios}
              aoFechar={(gravou) => { setConcluindo(null); if (gravou) depois(); }}
            />
          </div>
        ) : null)}
        /* O painel de criar entra logo DEPOIS da linha de onde saiu — é ali que
           o olho está, e a posição já diz que a nova etapa vem abaixo. */
        slotDepoisDaLinha={(_nivel, daLinha) =>
          criandoApos && daLinha.some((e) => e.id === criandoApos) ? painelCriar : null
        }
      />

          {/* Do zero (ou sem nenhuma etapa ainda): o painel vai no fim da lista. */}
          {criandoApos === 0 && painelCriar}
        </div>
        {/* No celular o resumo vem DEPOIS da lista: quem abre no telefone quer a
            etapa, não o placar. `order-lg-0` o devolve para o lado no desktop. */}
        <div className="col-12 col-lg-4 col-xl-3 order-last order-lg-0">{lateral}</div>
      </div>
    </>
  );
}
